import { APP_SEMANTIC } from '@/constant/theme/colors.constant';

// ─── PillConfig shape ─────────────────────────────────────────────────────────
/**
 * A single pill's visual configuration.
 * All keys are Tailwind utility strings.
 */
export interface PillConfig {
  /** Background colour — e.g. "bg-emerald-100 dark:bg-emerald-900/30" */
  bg: string;
  /** Text colour — e.g. "text-emerald-700 dark:text-emerald-400" */
  text: string;
  /** Dot / indicator colour — e.g. "bg-emerald-500" */
  dot?: string;
  /** Ring / border colour for the pill outline */
  border?: string;
  /** Left accent bar colour for shape="bar" — e.g. "bg-blue-500" */
  bar?: string;
  /** Animates the dot with ping pulse (live / in-progress states) */
  pulse?: boolean;
}

// ─── Semantic pill tokens ─────────────────────────────────────────────────────
// Derived from APP_SEMANTIC so all pill colours track the central theme palette.
// Only `pulse`, `bar`, and special overrides are added inline per-entry.
const P = {
  success: { bg: APP_SEMANTIC.success.bg, text: APP_SEMANTIC.success.text, border: APP_SEMANTIC.success.border, dot: APP_SEMANTIC.success.dot },
  danger: { bg: APP_SEMANTIC.danger.bg, text: APP_SEMANTIC.danger.text, border: APP_SEMANTIC.danger.border, dot: APP_SEMANTIC.danger.dot },
  warning: { bg: APP_SEMANTIC.warning.bg, text: APP_SEMANTIC.warning.text, border: APP_SEMANTIC.warning.border, dot: APP_SEMANTIC.warning.dot },
  info: { bg: APP_SEMANTIC.info.bg, text: APP_SEMANTIC.info.text, border: APP_SEMANTIC.info.border, dot: APP_SEMANTIC.info.dot },
  neutral: { bg: APP_SEMANTIC.neutral.bg, text: APP_SEMANTIC.neutral.text, border: APP_SEMANTIC.neutral.border, dot: APP_SEMANTIC.neutral.dot },
  rose: { bg: APP_SEMANTIC.rose.bg, text: APP_SEMANTIC.rose.text, border: APP_SEMANTIC.rose.border, dot: APP_SEMANTIC.rose.dot },
  sky: { bg: APP_SEMANTIC.sky.bg, text: APP_SEMANTIC.sky.text, border: APP_SEMANTIC.sky.border, dot: APP_SEMANTIC.sky.dot },
  indigo: { bg: APP_SEMANTIC.indigo.bg, text: APP_SEMANTIC.indigo.text, border: APP_SEMANTIC.indigo.border, dot: APP_SEMANTIC.indigo.dot },
  teal: { bg: APP_SEMANTIC.teal.bg, text: APP_SEMANTIC.teal.text, border: APP_SEMANTIC.teal.border, dot: APP_SEMANTIC.teal.dot },
} as const;

// ─── Preset maps ──────────────────────────────────────────────────────────────

export const STATUS_CODE_PILL_MAP: Record<number, PillConfig> = {
  200: { bg: P.success.bg, text: P.success.text },
  201: { bg: P.success.bg, text: P.success.text },
  100: { bg: P.info.bg, text: P.info.text },
  422: { bg: P.danger.bg, text: P.danger.text },
  500: { bg: P.warning.bg, text: P.warning.text },
  0: { bg: P.neutral.bg, text: P.neutral.text },
};

export const ALERT_STATUS_PILL_MAP: Record<string, PillConfig> = {
  OPEN: { ...P.warning, pulse: true },
  CLOSED: { ...P.success },
  FAILED: { ...P.danger },
  PENDING: { ...P.indigo, pulse: true },
};

export const COOLER_STATUS_PILL_MAP: Record<string, PillConfig> = {
  PROCESSED: { ...P.success },
  INPROGRESS: { ...P.warning, pulse: true },
  FAILED: { ...P.danger },
  INVALID: { ...P.neutral },
};

export const COOLER_TYPE_PILL_MAP: Record<string, PillConfig> = {
  UNLOCK: { ...P.info, bar: P.info.dot, border: 'border-transparent' },
  RESTOCK: { ...P.success, bar: P.success.dot, border: 'border-transparent' },
  TEST: { ...P.rose, bar: P.rose.dot, border: 'border-transparent' },
  TRAINING: { ...P.warning, bar: P.warning.dot, border: 'border-transparent' },
  'LOCAL-UNLOCK': { ...P.indigo, bar: P.indigo.dot, border: 'border-transparent' },
};

export const CRON_STATUS_PILL_MAP: Record<string, PillConfig> = {
  SUCCESS: { ...P.success },
  COMPLETED: { ...P.success },
  FAILED: { ...P.danger },
  ERROR: { ...P.danger },
  RUNNING: { ...P.info, pulse: true },
  IN_PROGRESS: { ...P.info, pulse: true },
};

export const ACTIVE_INACTIVE_PILL_MAP: Record<string, PillConfig> = {
  active: { ...P.success },
  inactive: { ...P.danger },
};

export const VDI_SYNC_PILL_MAP: Record<string, PillConfig> = {
  SUCCESS: { ...P.success, pulse: true },
  FAILURE: { ...P.rose },
};

export const BOOL_ENABLED_PILL_MAP: Record<string, PillConfig> = {
  ENABLED: { bg: P.sky.bg, text: P.sky.text, border: P.sky.border },
  DISABLED: { bg: P.rose.bg, text: P.rose.text, border: P.rose.border },
};

export const STATUS_ENABLED_PILL_MAP: Record<string, PillConfig> = {
  ENABLED: { ...P.success },
  DISABLED: { ...P.neutral },
};

export const DEFAULT_PILL_CONFIG: PillConfig = { ...P.neutral };
export const ACTIVE_PILL_CONFIG: PillConfig = { bg: P.success.bg, text: P.success.text, border: P.success.border };
export const INACTIVE_PILL_CONFIG: PillConfig = { bg: P.danger.bg, text: P.danger.text, border: P.danger.border };

// ─── SubApp helpers ───────────────────────────────────────────────────────────
// All colors sourced from APP_SEMANTIC. No green, red, or gray.
// 8 visually distinct hues — palette order matches subapp.util.ts SUBAPP_PALETTE.
const SUBAPP_CONFIG_MAP: Record<string, PillConfig> = {
  blue: { bg: APP_SEMANTIC.blue.badge, text: '', dot: APP_SEMANTIC.blue.dot },
  purple: { bg: APP_SEMANTIC.purple.badge, text: '', dot: APP_SEMANTIC.purple.dot },
  orange: { bg: APP_SEMANTIC.orange.badge, text: '', dot: APP_SEMANTIC.orange.dot },
  indigo: { bg: APP_SEMANTIC.indigo.badge, text: '', dot: APP_SEMANTIC.indigo.dot },
  warning: { bg: APP_SEMANTIC.warning.badge, text: '', dot: APP_SEMANTIC.warning.dot },
  sky: { bg: APP_SEMANTIC.sky.badge, text: '', dot: APP_SEMANTIC.sky.dot },
  cyan: { bg: APP_SEMANTIC.cyan.badge, text: '', dot: APP_SEMANTIC.cyan.dot },
  info: { bg: APP_SEMANTIC.info.badge, text: '', dot: APP_SEMANTIC.info.dot },
  fuchsia: { bg: APP_SEMANTIC.fuchsia.badge, text: '', dot: APP_SEMANTIC.fuchsia.dot },
  gray: { bg: APP_SEMANTIC.neutral.badge, text: '', dot: APP_SEMANTIC.neutral.dot },
};

/** Returns a PillConfig for a SubApplication name using its colour token. */
export function getSubAppPillConfig(color: string): PillConfig {
  return SUBAPP_CONFIG_MAP[color] ?? SUBAPP_CONFIG_MAP.gray;
}

/**
 * Converts a pastel PillConfig into a solid/filled variant.
 * Extracts the Tailwind color name from the `bg` class and produces
 * a fully-saturated background with white text.
 */
export function solidifyConfig(cfg: PillConfig): PillConfig {
  const match = cfg.bg.match(/bg-([a-z]+)-\d+/);
  const color = match?.[1] ?? 'gray';
  return {
    bg: `bg-${color}-500 dark:bg-${color}-500`,
    text: 'text-white',
    dot: 'bg-white',
    ...(cfg.bar ? { bar: `bg-${color}-500` } : {}),
  };
}

/** Range-based resolver for VDI / cooler status codes. */
export function resolveStatusCodePill(code: number | undefined): PillConfig {
  if (code === 0 || code === 100 || code === 200 || code === 201)
    return { bg: P.success.bg, text: P.success.text };
  if (code !== undefined && code >= 500)
    return { bg: P.warning.bg, text: P.warning.text };
  if (code !== undefined && code > 0)
    return { bg: P.danger.bg, text: P.danger.text };
  return DEFAULT_PILL_CONFIG;
}

// ─── App Log status pills ─────────────────────────────────────────────────────
export const APP_LOG_STATUS_PILL_MAP: Record<string, PillConfig> = {
  ACCEPTED: { ...P.info, pulse: true },
  PROCESSED: { ...P.success },
  FAILED: { ...P.danger },
};

// ─── Action Log status pills ──────────────────────────────────────────────────
export const ACTION_LOG_STATUS_PILL_MAP: Record<string, PillConfig> = {
  COMPLETED: { ...P.success },
  FAILED: { ...P.danger },
  ACCEPTED: { ...P.indigo, pulse: true },
  IN_PROGRESS: { ...P.indigo, pulse: true },
  PENDING: { ...P.warning, pulse: true },
};

// ─── Smart Cooler connection log event pills ──────────────────────────────────
export const CONNECTION_LOG_EVENT_PILL_MAP: Record<string, PillConfig> = {
  CONNECTED: { ...P.success },
  DISCONNECTED: { ...P.danger },
  HEARTBEAT: { ...P.sky, pulse: true },
  FAILED_HEARTBEAT: { ...P.warning },
};

export const SMART_COOLER_DEVICE_STATUS_PILL_MAP: Record<string, PillConfig> = {
  CONNECTED: { ...P.success, pulse: true },
  DISCONNECTED: { ...P.danger },
  PENDING: { ...P.warning, pulse: true },
  ERROR: { ...P.rose },
};

// ─── Smart Cooler system state pills ─────────────────────────────────────────
export const SMART_COOLER_SYSTEM_STATE_PILL_MAP: Record<string, PillConfig> = {
  ONLINE: { ...P.teal },
  OFFLINE: { ...P.neutral },
  UNKNOWN: { ...P.neutral },
};
